import { marked } from "marked";

export type ClinicalCriteriaSectionTone =
  | "overview"
  | "diagnostic"
  | "laboratory"
  | "classification";

export type ClinicalCriteriaSection = {
  id: string;
  title: string;
  html: string;
  tone: ClinicalCriteriaSectionTone;
};

export type ParsedClinicalCriteria = {
  title: string;
  sections: ClinicalCriteriaSection[];
};

export const normalizeClinicalCriteriaText = (text: string): string =>
  text.replace(/\s+/g, " ").trim();

export const detectClinicalCriteriaSectionTone = (
  title: string
): ClinicalCriteriaSectionTone => {
  const normalized = title.toLowerCase();
  if (/\boverview\b/.test(normalized)) return "overview";
  if (
    /diagnostic criteria/.test(normalized) ||
    /^i[\.\):\-]/.test(normalized.trim())
  ) {
    return "diagnostic";
  }
  if (
    /laboratory|imaging findings/.test(normalized) ||
    /^ii[\.\):\-]/.test(normalized.trim())
  ) {
    return "laboratory";
  }
  return "classification";
};

const isHeadingText = (text: string): boolean => {
  const normalized = normalizeClinicalCriteriaText(text);
  if (!normalized) return false;
  if (/\boverview\b/i.test(normalized)) return true;
  if (/^[ivxlc]+\./i.test(normalized)) return true;
  if (/^(I{1,3}|IV|V|VI|VII|VIII|IX|X)[\.\):\-]/i.test(normalized)) {
    return true;
  }
  return (
    /diagnostic criteria|laboratory|imaging findings|classification|severity|intensity/i.test(
      normalized
    ) && normalized.length < 120
  );
};

const isSectionHeadingElement = (element: Element): boolean => {
  const tag = element.tagName.toLowerCase();
  if (tag === "h2" || tag === "h3" || tag === "h4") return true;

  if (tag === "p") {
    const strong = element.querySelector("strong, b");
    if (!strong) return false;
    const strongText = normalizeClinicalCriteriaText(strong.textContent || "");
    const paragraphText = normalizeClinicalCriteriaText(
      element.textContent || ""
    );
    if (!isHeadingText(strongText)) return false;
    return (
      strongText === paragraphText ||
      paragraphText.startsWith(strongText) ||
      paragraphText.length - strongText.length < 8
    );
  }

  if (tag === "strong" || tag === "b") {
    return isHeadingText(
      normalizeClinicalCriteriaText(element.textContent || "")
    );
  }

  return false;
};

const extractSectionTitle = (element: Element): string => {
  const tag = element.tagName.toLowerCase();
  if (tag === "p") {
    const strong = element.querySelector("strong, b");
    if (strong) return normalizeClinicalCriteriaText(strong.textContent || "");
  }
  return normalizeClinicalCriteriaText(element.textContent || "");
};

const serializeNode = (node: ChildNode): string => {
  if (node.nodeType === Node.ELEMENT_NODE) {
    return (node as Element).outerHTML;
  }
  if (node.nodeType === Node.TEXT_NODE) {
    const text = (node.textContent || "").trim();
    return text ? `<p>${text}</p>` : "";
  }
  return "";
};

export const parseClinicalCriteriaHtml = (
  html: string,
  fallbackTitle?: string
): ParsedClinicalCriteria => {
  const defaultTitle = (fallbackTitle || "").trim() || "Clinical Criteria";

  if (!html.trim()) {
    return { title: defaultTitle, sections: [] };
  }

  if (typeof document === "undefined") {
    return {
      title: defaultTitle,
      sections: [
        {
          id: "section-0",
          title: "Overview",
          html,
          tone: "overview",
        },
      ],
    };
  }

  const doc = new DOMParser().parseFromString(html, "text/html");
  const body = doc.body;
  const removable = body.querySelectorAll("script, style, link, meta");
  for (let i = 0; i < removable.length; i += 1) {
    const node = removable[i];
    if (node.parentNode) node.parentNode.removeChild(node);
  }

  let title = defaultTitle;
  const h1 = body.querySelector("h1");
  if (h1) {
    title = normalizeClinicalCriteriaText(h1.textContent || "") || title;
    h1.remove();
  }

  const sections: ClinicalCriteriaSection[] = [];
  let currentTitle = "";
  let currentHtml: string[] = [];
  let sectionIndex = 0;

  const flushSection = () => {
    const content = currentHtml.join("").trim();
    if (!content && !currentTitle) return;

    const sectionTitle =
      currentTitle ||
      (sections.length === 0 ? "Overview" : `Section ${sectionIndex + 1}`);

    sections.push({
      id: `section-${sectionIndex}`,
      title: sectionTitle,
      html: content,
      tone: detectClinicalCriteriaSectionTone(sectionTitle),
    });
    sectionIndex += 1;
    currentTitle = "";
    currentHtml = [];
  };

  const children = Array.from(body.childNodes);
  for (let i = 0; i < children.length; i += 1) {
    const child = children[i];
    if (
      child.nodeType === Node.ELEMENT_NODE &&
      isSectionHeadingElement(child as Element)
    ) {
      flushSection();
      currentTitle = extractSectionTitle(child as Element);
      continue;
    }

    const serialized = serializeNode(child);
    if (serialized) currentHtml.push(serialized);
  }

  flushSection();

  if (sections.length === 0) {
    sections.push({
      id: "section-0",
      title: "Overview",
      html: body.innerHTML.trim(),
      tone: "overview",
    });
  }

  return { title, sections };
};

const SECTION_SPLIT_REGEX = /(?=^#{2,3}\s+)/m;
const HAS_HEADING_REGEX = /#{2,3}\s+\S/;

const decodeEscapedWhitespace = (text: string): string => {
  if (!/\\[nrt]/.test(text)) return text;
  return text.replace(/\\([nrt])/g, (_match, token: string) => {
    if (token === "n") return "\n";
    if (token === "r") return "\r";
    if (token === "t") return "\t";
    return "\\";
  });
};

const stripSingleParagraphWrapper = (text: string): string => {
  const trimmed = text.trim();
  const paragraphMatch = trimmed.match(/^<p[^>]*>([\s\S]*)<\/p>$/i);
  if (paragraphMatch) return paragraphMatch[1].trim();
  return trimmed;
};

const sanitizeLiteralHtmlBreaks = (text: string): string =>
  text.replace(/&lt;br\s*\/?&gt;/gi, "\n").replace(/<br\s*\/?>/gi, "\n");

export const normalizeClinicalCriteriaSource = (raw: string): string => {
  let text = decodeEscapedWhitespace(raw.trim());
  text = sanitizeLiteralHtmlBreaks(text);
  text = stripSingleParagraphWrapper(text);
  return text.replace(/\r\n/g, "\n").replace(/\r/g, "\n");
};

const normalizeForSplitting = (text: string): string =>
  text
    .replace(/\s+###\s+/g, "\n### ")
    .replace(/\s+##\s+/g, "\n## ")
    .replace(/\s+\*\*/g, "\n**");

export const cleanMarkdownTitle = (raw: string): string =>
  raw
    .replace(/^#{1,6}\s*/, "")
    .replace(/^[*-]+\s*/, "")
    .replace(/\s+/g, " ")
    .trim();

const isValidSectionTitle = (title: string): boolean => {
  const cleaned = cleanMarkdownTitle(title);
  return cleaned.length > 1 && /[a-zA-Z0-9]/.test(cleaned);
};

const isValidSectionBody = (body: string): boolean =>
  body.replace(/[-*#\s]/g, "").length > 0;

export const looksLikeClinicalCriteriaMarkdown = (content: string): boolean => {
  const text = normalizeForSplitting(normalizeClinicalCriteriaSource(content));
  if (!text) return false;
  if (HAS_HEADING_REGEX.test(text)) return true;
  return /^[-*]\s+/m.test(text) && !/<[a-z][\s>]/i.test(text);
};

export const cleanSectionMarkdown = (content: string): string =>
  content
    .replace(/(\n|\.|:)\s+-\s+/g, "$1\n- ")
    .replace(/^[-*]\s*\n+\s*/gm, "- ")
    .replace(/^[-*]\s*$/gm, "")
    .replace(/\n{3,}/g, "\n\n")
    .trim();

const cleanRenderedListHtml = (html: string): string =>
  html.replace(/<li>\s*<\/li>/gi, "");

export const markdownToClinicalCriteriaHtml = (markdown: string): string => {
  const cleanContent = cleanSectionMarkdown(markdown);
  if (!cleanContent.trim()) return "";
  const html = marked.parse(cleanContent, {
    gfm: true,
    breaks: true,
  }) as string;
  return cleanRenderedListHtml(html);
};

const markdownFallbackHtml = (markdown: string): string => {
  const html = markdownToClinicalCriteriaHtml(markdown);
  if (html.trim()) return html;
  const escaped = cleanSectionMarkdown(markdown)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
  return `<div class="whitespace-pre-wrap">${escaped}</div>`;
};

const extractDocumentTitle = (text: string, fallbackTitle: string): string => {
  const lines = text.split("\n");
  let firstLine = "";
  for (let i = 0; i < lines.length; i += 1) {
    if (lines[i].trim()) {
      firstLine = lines[i].trim();
      break;
    }
  }
  return cleanMarkdownTitle(firstLine) || fallbackTitle;
};

const splitSectionTitleAndBody = (
  chunk: string
): { title: string; body: string } => {
  const withoutHeader = chunk.replace(/^#{2,3}\s+/, "").trim();
  const lines = withoutHeader.split("\n");
  const firstLine = (lines[0] || "").trim();

  if (lines.length > 1) {
    return {
      title: firstLine,
      body: lines.slice(1).join("\n").trim(),
    };
  }

  const inlineMatch = firstLine.match(/^(.+?)(?=\s+\*\*|\s+-\s+)/);
  if (inlineMatch) {
    return {
      title: inlineMatch[1].trim(),
      body: firstLine.slice(inlineMatch[1].length).trim(),
    };
  }

  return { title: firstLine, body: "" };
};

const parseSectionChunk = (
  chunk: string,
  index: number,
  documentTitle: string
): ClinicalCriteriaSection | null => {
  const split = splitSectionTitleAndBody(chunk);
  const sectionTitle = cleanMarkdownTitle(split.title);

  if (!isValidSectionTitle(sectionTitle)) return null;

  const cleanedBody = cleanSectionMarkdown(split.body);
  const firstBodyLine = (cleanedBody.split("\n")[0] || "").trim();
  const duplicateTitle = cleanMarkdownTitle(firstBodyLine);
  if (
    !isValidSectionBody(cleanedBody) ||
    (duplicateTitle === documentTitle && cleanedBody.split("\n").length <= 1)
  ) {
    return null;
  }

  return {
    id: `section-${index}`,
    title: sectionTitle,
    html: markdownFallbackHtml(cleanedBody),
    tone: detectClinicalCriteriaSectionTone(sectionTitle),
  };
};

const isSectionHeadingChunk = (chunk: string): boolean =>
  /^#{2,3}\s+/.test(chunk);

export const parseClinicalCriteriaMarkdown = (
  markdown: string,
  fallbackTitle?: string
): ParsedClinicalCriteria => {
  const source = normalizeForSplitting(
    normalizeClinicalCriteriaSource(markdown)
  );
  const defaultTitle = (fallbackTitle || "").trim() || "Clinical Criteria";

  if (!source) {
    return { title: defaultTitle, sections: [] };
  }

  if (!HAS_HEADING_REGEX.test(source)) {
    const lines = source.split("\n");
    let firstLine = "";
    let firstIndex = -1;
    for (let i = 0; i < lines.length; i += 1) {
      if (lines[i].trim()) {
        firstLine = lines[i].trim();
        firstIndex = i;
        break;
      }
    }
    const body = firstIndex >= 0 ? lines.slice(firstIndex + 1).join("\n").trim() : "";
    const cleanedBody = cleanSectionMarkdown(body);
    if (cleanedBody && isValidSectionBody(cleanedBody)) {
      return {
        title: extractDocumentTitle(firstLine, defaultTitle),
        sections: [
          {
            id: "section-0",
            title: "Overview",
            html: markdownFallbackHtml(cleanedBody),
            tone: "overview",
          },
        ],
      };
    }
    return {
      title: extractDocumentTitle(firstLine, defaultTitle),
      sections: [],
    };
  }

  const rawChunks = source.split(SECTION_SPLIT_REGEX);
  const chunks: string[] = [];
  for (let i = 0; i < rawChunks.length; i += 1) {
    const chunk = rawChunks[i].trim();
    if (chunk) chunks.push(chunk);
  }

  let documentTitle = defaultTitle;
  let sectionChunks = chunks;

  if (chunks[0] && !isSectionHeadingChunk(chunks[0])) {
    documentTitle = extractDocumentTitle(chunks[0], defaultTitle);
    sectionChunks = chunks.slice(1);
  }

  if (sectionChunks.length === 0) {
    return { title: documentTitle, sections: [] };
  }

  const sections: ClinicalCriteriaSection[] = [];
  for (let i = 0; i < sectionChunks.length; i += 1) {
    const section = parseSectionChunk(sectionChunks[i], i, documentTitle);
    if (section) sections.push(section);
  }

  return { title: documentTitle, sections };
};

export const parseClinicalCriteriaContent = (
  content: string,
  fallbackTitle?: string
): ParsedClinicalCriteria => {
  const normalized = normalizeClinicalCriteriaSource(content);
  if (looksLikeClinicalCriteriaMarkdown(normalized)) {
    return parseClinicalCriteriaMarkdown(normalized, fallbackTitle);
  }
  return parseClinicalCriteriaHtml(content, fallbackTitle);
};

export const criteriaFileToViewerSource = (file: {
  markdown?: string;
  html?: string | null;
} | null): string => {
  if (!file) return "";
  const markdown = (file.markdown || "").trim();
  const html = (file.html || "").trim();
  if (markdown) return markdown;
  if (!html) return "";
  const normalized = normalizeClinicalCriteriaSource(html);
  if (looksLikeClinicalCriteriaMarkdown(normalized)) return normalized;
  return normalized || html;
};
